import { useState, type Dispatch } from 'react';

import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';
import { failToggleFields } from './state/loggerDraftHelpers';
import { kindNeedsDuration } from './deriveEntryKind';
import { parseLocaleNumber } from '@heraion/shared';

// Fail-confirm prompt (AP-0p model, 2026-07-06). When a rep-based set is
// failed, confirm the rep count: "you did N reps, 1 failed → save N-1". The
// failed rep is always exactly one, so N-1 is the default; the stepper lets
// the athlete correct it (e.g. 2 clean, not N-1). On confirm we write `reps`
// = the clean count — `fail` itself is a pure label that changes no math.
// Cardio/timed have no rep count to decrement → no prompt.
export function useFailConfirm(args: {
  entries: DraftEntry[];
  dispatch: Dispatch<LoggerAction>;
}) {
  const { entries, dispatch } = args;
  // `attempted` = the reps entered before the fail (N); the popup shows it
  // for context and defaults the saved count to N-1.
  const [prompt, setPrompt] = useState<{
    entryIdx: number;
    setIdx: number;
    attempted: number;
  } | null>(null);

  const maybePrompt = (entryIdx: number, setIdx: number) => {
    const entry = entries[entryIdx];
    if (!entry || kindNeedsDuration(entry)) return;
    const attempted = Math.round(
      parseLocaleNumber(entry.sets[setIdx]?.reps ?? ''),
    );
    if (!Number.isFinite(attempted) || attempted <= 0) return;
    setPrompt({ entryIdx, setIdx, attempted });
  };

  // Save: apply the decrement (reps = the clean count) and keep the fail.
  const confirm = (savedReps: number) => {
    if (!prompt) return;
    dispatch({
      type: 'update_set',
      entryIdx: prompt.entryIdx,
      setIdx: prompt.setIdx,
      patch: { reps: String(Math.max(0, savedReps)) },
    });
    setPrompt(null);
  };

  // Dismiss (back / backdrop) = "it wasn't a fail" → clear the fail label and
  // leave `reps` untouched (the full attempted count), so a mis-tap of Fail is
  // a clean escape with no silent rep loss. The set stays completed.
  const cancel = () => {
    if (!prompt) return;
    dispatch({
      type: 'update_set',
      entryIdx: prompt.entryIdx,
      setIdx: prompt.setIdx,
      patch: failToggleFields(false),
    });
    setPrompt(null);
  };

  return { prompt, maybePrompt, confirm, cancel };
}
